import { RFX_RELATIONSHIPS, type RfxRelationship } from "../types/rfx-list.type";

/**
 * Relationship-keyed copy for the read-only preview banner. Server-authoritative:
 * driven ONLY by `access_flags.relationship`, never by an FE identity heuristic
 * (the `external && !user_id && !user` guest-detection pattern is explicitly
 * banned on this surface — see `2026-06-30-server-authoritative-flags-not-heuristics`).
 *
 * `owner`/`approver` never reach this surface in practice (the `previewReadOnly`
 * ability does not grant them — see Task 004 summary), but the map stays
 * exhaustive over the full `RfxRelationship` union so a backend enum addition
 * fails loudly here instead of silently rendering nothing.
 */
const RELATIONSHIP_BANNER_COPY: Record<RfxRelationship, string> = {
	owner: "You are viewing a read-only preview of this RFx.",
	approver: "You are viewing a read-only preview of this RFx.",
	panel_member:
		"You are viewing this RFx as a cross-company panel member. This is a read-only preview.",
	guest:
		"You have been invited to this RFx. This is a read-only preview — accept the invitation to respond.",
};

export class UnrecognisedRfxRelationshipError extends Error {
	constructor(relationship: string) {
		super(`Unrecognised RfxRelationship value: "${relationship}"`);
		this.name = "UnrecognisedRfxRelationshipError";
	}
}

const isRfxRelationship = (value: string): value is RfxRelationship =>
	(RFX_RELATIONSHIPS as readonly string[]).includes(value);

/**
 * Resolves the preview banner copy for a `relationship` value. Throws
 * `UnrecognisedRfxRelationshipError` for any value outside the known
 * `RfxRelationship` union rather than silently rendering nothing.
 */
export const getRelationshipBannerCopy = (relationship: string): string => {
	if (!isRfxRelationship(relationship)) {
		throw new UnrecognisedRfxRelationshipError(relationship);
	}

	return RELATIONSHIP_BANNER_COPY[relationship];
};
